import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import { apiService } from "../services/apiService";
import SingleProductEvaluator from "./SearchTable";

const DashboardContainer = styled.div`
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const Controls = styled.div`
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    max-width: 1200px;
    margin: 0 auto 2rem auto;
    
    @media (max-width: 768px) {
        padding: 1rem;
        gap: 1rem;
        flex-direction: column;
        align-items: stretch;
    }
`;

// Commented out unused styled components
/*
const ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    font-weight: 500;
    color: #2c3e50;
    font-size: 0.9rem;
`;

const Select = styled.select`
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    min-width: 150px;

    &:focus {
        outline: none;
        border-color: #3498db;
    }
`;

const Button = styled.button`
    background-color: ${(props) =>
        props.variant === "primary" ? "#3498db" : "#27ae60"};
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;

    &:hover {
        background-color: ${(props) =>
            props.variant === "primary" ? "#2980b9" : "#229954"};
    }

    &:disabled {
        background-color: #bdc3c7;
        cursor: not-allowed;
    }
`;
*/

const StatsContainer = styled.div`
    display: flex;
    gap: 1rem;
    
    @media (max-width: 768px) {
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.5rem;
    }
`;

// Removed SearchProgress - now using StatCard format

const StatCard = styled.div`
    background: ${(props) => props.color || "#ecf0f1"};
    color: ${(props) => props.textColor || "#2c3e50"};
    padding: 0.75rem 1rem;
    border-radius: 4px;
    text-align: center;
    min-width: 80px;
`;

const StatLabel = styled.div`
    font-size: 0.8rem;
    opacity: 0.8;
`;

const StatValue = styled.div`
    font-size: 1.2rem;
    font-weight: 600;
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 3rem;
    color: #7f8c8d;
    font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
    background: #fee;
    color: #c0392b;
    padding: 1rem;
    border-radius: 4px;
    margin: 1rem 0;
    border-left: 4px solid #e74c3c;
`;

const BackgroundSaveIndicator = styled.div`
    position: fixed;
    top: 20px;
    right: 20px;
    background: white;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    z-index: 1000;
    transition: all 0.3s ease;
    
    @media (max-width: 768px) {
        top: 10px;
        right: 10px;
        left: 10px;
        padding: 0.5rem 0.75rem;
        font-size: 0.9rem;
    }
    
    ${props => props.count === 0 && `
        opacity: 0;
        transform: translateX(100%);
        pointer-events: none;
    `}
`;

const SaveStatusIcon = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    ${props => props.status === 'saving' && `
        background: #f39c12;
        animation: pulse 1.5s infinite;
    `}
    
    ${props => props.status === 'success' && `
        background: #27ae60;
        color: white;
        font-size: 10px;
    `}
    
    ${props => props.status === 'error' && `
        background: #e74c3c;
        color: white;
        font-size: 10px;
    `}
    
    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
    }
`;

const SaveStatusText = styled.span`
    font-size: 0.9rem;
    color: #2c3e50;
    font-weight: 500;
`;

function Dashboard({ currentUser }) {
    const [availableDays, setAvailableDays] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [data, setData] = useState(null);
    const [annotations, setAnnotations] = useState({});
    const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    // const [isSaving, setIsSaving] = useState(false); // Commented out - only used in hidden saveAnnotations function
    const [error, setError] = useState("");
    const [userProgress, setUserProgress] = useState(null);
    const [isLoadingProgress, setIsLoadingProgress] = useState(false);
    
    // Background save tracking
    const [backgroundSaves, setBackgroundSaves] = useState(new Map());
    const [backgroundSaveCount, setBackgroundSaveCount] = useState(0);

    const loadAvailableDays = useCallback(async () => {
        const result = await apiService.getAvailableDays();
        if (result.success) {
            // Sort dates in ascending order (oldest first)
            const sortedDates = result.data.sort((a, b) => new Date(a) - new Date(b));
            setAvailableDays(sortedDates);
        } else {
            toast.error(result.error);
        }
    }, []);

    const loadUserProgress = useCallback(async () => {
        if (!currentUser) return;
        
        setIsLoadingProgress(true);
        const result = await apiService.getUserProgress();
        if (result.success) {
            setUserProgress(result.data);
        } else {
            // Set default progress if loading fails
            setUserProgress({
                username: currentUser.username,
                current_date: "",
                current_search_index: 0,
                total_searches_completed: 0,
                total_annotations_completed: 0
            });
            toast.info("Starting fresh - no previous progress found");
        }
        setIsLoadingProgress(false);
    }, [currentUser]);

    const loadDataForDate = useCallback(async (date, searchIndex = 0, welcomeMessage = null) => {
        if (!date) return;

        setIsLoading(true);
        setError("");

        const result = await apiService.getDataForDate(date);
        if (result.success) {
            setData(result.data);
            
            // Extract user-specific annotations
            const userAnnotationKey = currentUser ? `${currentUser.username}-annotation` : '';
            const userAnnotations = result.data.annotations?.[userAnnotationKey] || {};
            
            setAnnotations(userAnnotations);
            
            setCurrentSearchIndex(searchIndex || 0); // Reset to specified search or default
            
            // Show welcome message only when explicitly provided
            if (welcomeMessage) {
                toast.success(welcomeMessage);
            }
        } else {
            setError(result.error);
            toast.error(result.error);
        }

        setIsLoading(false);
    }, [currentUser]);

    useEffect(() => {
        loadAvailableDays();
    }, [loadAvailableDays]);

    useEffect(() => {
        // Load user progress when currentUser becomes available
        if (currentUser && !userProgress) {
            loadUserProgress();
        }
    }, [currentUser, userProgress, loadUserProgress]);

    useEffect(() => {
        // Once we have both user and progress, load the appropriate data
        if (currentUser && userProgress && availableDays.length > 0 && !selectedDate) {
            // Check if user has saved progress
            if (userProgress.current_date && availableDays.includes(userProgress.current_date)) {
                // Restore user's progress
                setSelectedDate(userProgress.current_date);
                loadDataForDate(userProgress.current_date, userProgress.current_search_index, `Welcome back! Resuming from ${userProgress.current_date}`);
            } else if (availableDays.length > 0) {
                // If no progress or invalid date, start with the oldest date
                const oldestDate = availableDays[0];
                setSelectedDate(oldestDate);
                loadDataForDate(oldestDate, 0, `Welcome! Starting from ${oldestDate}`);
            }
        }
    }, [currentUser, userProgress, availableDays, selectedDate, loadDataForDate]);

    const getStats = useCallback(() => {
        if (!data || !data.raw_data)
            return { total: 0, annotated: 0, good: 0, bad: 0, searchesCompleted: 0 };

        let total = 0;
        let annotated = 0;
        let good = 0;
        let bad = 0;
        let searchesCompleted = 0;

        data.raw_data.forEach((search, searchIndex) => {
            let searchAnnotatedCount = 0;
            search.results.forEach((result, resultIndex) => {
                total++;
                const annotation =
                    annotations[`search_${searchIndex}`]?.[
                        `result_${resultIndex}`
                    ];
                if (annotation && annotation.rating) {
                    annotated++;
                    searchAnnotatedCount++;
                    if (annotation.rating === "good") good++;
                    if (annotation.rating === "bad") bad++;
                }
            });
            // Count search as completed if it has at least one annotation
            if (searchAnnotatedCount > 0) {
                searchesCompleted++;
            }
        });

        return { total, annotated, good, bad, searchesCompleted };
    }, [data, annotations]);

    const saveUserProgress = useCallback(async (date, searchIndex) => {
        if (!currentUser || !date) return;
        
        const stats = getStats();
        await apiService.saveUserProgress(
            date, 
            searchIndex, 
            stats.searchesCompleted,
            stats.annotated
        );
    }, [currentUser, getStats]);

    // Commented out unused function - only used in hidden date selector
    /*
    const handleDateChange = (date) => {
        if (!date) return;
        
        setSelectedDate(date);
        setData(null); // Clear current data
        setAnnotations({});
        setCurrentSearchIndex(0);
        
        // Load data for the new date
        loadDataForDate(date, 0);
        
        // Save progress when user manually changes date
        saveUserProgress(date, 0);
    };
    */

    const handleAnnotationChange = (searchIndex, resultIndex, annotation) => {
        setAnnotations((prev) => ({
            ...prev,
            [`search_${searchIndex}`]: {
                ...prev[`search_${searchIndex}`],
                [`result_${resultIndex}`]: annotation,
            },
        }));
    };

    // Commented out unused function - only used in hidden Save All Annotations button
    /*
    const saveAnnotations = async () => {
        if (!selectedDate || !data || !currentUser) {
            return;
        }

        setIsSaving(true);

        // Structure data with user-specific annotation key
        const userAnnotationKey = `${currentUser.username}-annotation`;
        const dataToSave = {
            date: selectedDate,
            annotations: {
                [userAnnotationKey]: annotations
            },
            raw_data: data.raw_data,
        };

        const result = await apiService.saveAnnotations(
            selectedDate,
            dataToSave
        );
        if (result.success) {
            toast.success("Annotations saved successfully!");
        } else {
            toast.error(result.error);
        }

        setIsSaving(false);
    };
    */

    const handleSaveAndNext = async () => {
        if (!selectedDate || !data || !currentUser) {
            return;
        }

        // Structure data with user-specific annotation key and progress info
        const userAnnotationKey = `${currentUser.username}-annotation`;
        const stats = getStats();
        
        let nextDate = selectedDate;
        let nextSearchIndex = currentSearchIndex + 1;
        
        // Determine next position
        if (currentSearchIndex >= data.raw_data.length - 1) {
            // Move to next date if at end of current date
            const currentDateIndex = availableDays.indexOf(selectedDate);
            if (currentDateIndex !== -1 && currentDateIndex < availableDays.length - 1) {
                nextDate = availableDays[currentDateIndex + 1];
                nextSearchIndex = 0;
            }
        }
        
        const dataToSave = {
            date: selectedDate,
            annotations: {
                [userAnnotationKey]: annotations
            },
            raw_data: data.raw_data,
            // Include progress information
            current_search_index: nextSearchIndex,
            total_searches_completed: stats.searchesCompleted,
            total_annotations_completed: stats.annotated
        };

        // Create unique save ID for tracking
        const saveId = `${selectedDate}-${currentSearchIndex}-${Date.now()}`;
        
        // Move to next position immediately
        if (data && currentSearchIndex < data.raw_data.length - 1) {
            setCurrentSearchIndex(currentSearchIndex + 1);
        } else {
            // Move to next date
            const currentDateIndex = availableDays.indexOf(selectedDate);
            if (currentDateIndex !== -1 && currentDateIndex < availableDays.length - 1) {
                const nextDateValue = availableDays[currentDateIndex + 1];
                setSelectedDate(nextDateValue);
                loadDataForDate(nextDateValue, 0);
                toast.success(`Moving to next date: ${nextDateValue}`);
            } else {
                toast.success("All dates completed!");
            }
        }
        
        // Update local progress state immediately
        setUserProgress(prev => ({
            ...prev,
            current_date: nextDate,
            current_search_index: nextSearchIndex,
            total_searches_completed: stats.searchesCompleted,
            total_annotations_completed: stats.annotated
        }));
        
        // Start background save operation
        setBackgroundSaves(prev => new Map(prev).set(saveId, 'saving'));
        setBackgroundSaveCount(prev => prev + 1);
        
        // Perform save in background - don't await
        (async () => {
            try {
                const result = await apiService.saveAnnotationsAndProgress(
                    selectedDate,
                    dataToSave
                );
                
                if (result.success) {
                    // Update background save status to success
                    setBackgroundSaves(prev => new Map(prev).set(saveId, 'success'));
                    // Remove from count after short delay
                    setTimeout(() => {
                        setBackgroundSaves(prev => {
                            const newMap = new Map(prev);
                            newMap.delete(saveId);
                            return newMap;
                        });
                        setBackgroundSaveCount(prev => Math.max(0, prev - 1));
                    }, 2000);
                } else {
                    // Update background save status to error
                    setBackgroundSaves(prev => new Map(prev).set(saveId, 'error'));
                    toast.error(`Save failed: ${result.error}`);
                    // Keep error status visible longer
                    setTimeout(() => {
                        setBackgroundSaves(prev => {
                            const newMap = new Map(prev);
                            newMap.delete(saveId);
                            return newMap;
                        });
                        setBackgroundSaveCount(prev => Math.max(0, prev - 1));
                    }, 5000);
                }
            } catch (error) {
                // Handle network errors or other exceptions
                setBackgroundSaves(prev => new Map(prev).set(saveId, 'error'));
                toast.error(`Save failed: ${error.message}`);
                setTimeout(() => {
                    setBackgroundSaves(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(saveId);
                        return newMap;
                    });
                    setBackgroundSaveCount(prev => Math.max(0, prev - 1));
                }, 5000);
            }
        })();
    };

    const handleNavigate = (newIndex) => {
        if (data && newIndex >= 0 && newIndex < data.raw_data.length) {
            setCurrentSearchIndex(newIndex);
            // Save progress when manually navigating
            if (selectedDate) {
                saveUserProgress(selectedDate, newIndex);
            }
        }
    };

    const stats = getStats();

    return (
        <DashboardContainer>
            <Controls>
                {/* Hidden components - commented out but functionality preserved */}
                {/* 
                <ControlGroup>
                    <Label>Select Date</Label>
                    <Select
                        value={selectedDate}
                        onChange={(e) => handleDateChange(e.target.value)}
                        disabled={isLoadingProgress}
                    >
                        <option value="">Choose a date...</option>
                        {availableDays.map((date) => (
                            <option key={date} value={date}>
                                {date} {userProgress?.current_date === date ? "★" : ""}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <Button
                    variant="primary"
                    onClick={() => loadDataForDate(selectedDate, 0)}
                    disabled={!selectedDate || isLoading || isLoadingProgress}
                >
                    {isLoading ? "Loading..." : "Load Data"}
                </Button>

                <Button 
                    onClick={saveAnnotations} 
                    disabled={!data || isSaving || isLoadingProgress}
                >
                    {isSaving ? "Saving..." : "Save All Annotations"}
                </Button>
                */}

                <StatsContainer>
                    <StatCard>
                        <StatLabel>Search</StatLabel>
                        <StatValue>{currentSearchIndex + 1}/{data?.raw_data?.length || 0}</StatValue>
                    </StatCard>
                    <StatCard>
                        <StatLabel>Annotated</StatLabel>
                        <StatValue>{stats.annotated}</StatValue>
                    </StatCard>
                    <StatCard color="#fadbd8" textColor="#e74c3c">
                        <StatLabel>Bad</StatLabel>
                        <StatValue>{stats.bad}</StatValue>
                    </StatCard>
                    {userProgress && (
                        <StatCard color="#e8f4f8" textColor="#2980b9">
                            <StatLabel>Progress</StatLabel>
                            <StatValue>
                                {userProgress.current_date ? `${userProgress.current_date} #${userProgress.current_search_index + 1}` : "New"}
                            </StatValue>
                        </StatCard>
                    )}
                </StatsContainer>
            </Controls>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {(isLoading || isLoadingProgress) && (
                <LoadingMessage>
                    {isLoadingProgress ? "Loading your progress..." : `Loading data for ${selectedDate}...`}
                </LoadingMessage>
            )}

            {data && data.raw_data && !isLoading && !isLoadingProgress && (
                <SingleProductEvaluator
                    searches={data.raw_data}
                    annotations={annotations}
                    onAnnotationChange={handleAnnotationChange}
                    currentSearchIndex={currentSearchIndex}
                    onSaveAndNext={handleSaveAndNext}
                    onNavigate={handleNavigate}
                />
            )}

            {!data && !isLoading && !isLoadingProgress && selectedDate && (
                <LoadingMessage>
                    No data available for {selectedDate}
                </LoadingMessage>
            )}
            
            {/* Background save indicator */}
            <BackgroundSaveIndicator count={backgroundSaveCount}>
                {Array.from(backgroundSaves.entries()).map(([saveId, status]) => (
                    <SaveStatusIcon key={saveId} status={status}>
                        {status === 'success' && '✓'}
                        {status === 'error' && '✗'}
                    </SaveStatusIcon>
                ))}
                <SaveStatusText>
                    {backgroundSaveCount > 0 && (
                        backgroundSaveCount === 1 ? 'Saving...' : `${backgroundSaveCount} saves pending`
                    )}
                </SaveStatusText>
            </BackgroundSaveIndicator>
        </DashboardContainer>
    );
}

export default Dashboard;
